/**
 * ライセンスの画面（issue #486）。ログインの画面と、左の項目立ての下の「ライセンス」から開く。
 *
 * MIT・BSD・Apache-2.0 などは、配るときに著作権表示とライセンス文を添えることを求めています。
 * 一覧は、組み立て（npm run build）のたびに本番で使う部品から自動で作ったファイル
 * （`third-party-licenses.json`・scripts/third-party-licenses.mjs）を読んで出します。手で書きません。
 *
 * ログインしていなくても開けます（ライセンスの表示は、使う方が誰でも見られるようにしておくもの）。
 * 決めごと（決定22）: 読み込み中・読めなかったときを言葉で出し、「戻る」をはっきり置く。
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/Button.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';
import { LoadingBlock } from '../components/StatusBlock.js';

/** 一覧のファイル（管理画面と同じ場所から配る）。 */
export const LICENSES_JSON = `${import.meta.env.BASE_URL}third-party-licenses.json`;
/** 同じ中身のテキストファイル。 */
export const LICENSES_TEXT = `${import.meta.env.BASE_URL}third-party-licenses.txt`;

export interface LicensedPackage {
  name: string;
  version: string;
  license: string;
  note?: string;
  texts: { file: string; text: string }[];
}

type LoadState =
  { kind: 'loading' } | { kind: 'failed' } | { kind: 'ready'; packages: LicensedPackage[] };

async function loadLicenses(): Promise<LicensedPackage[]> {
  const res = await fetch(LICENSES_JSON, { cache: 'no-cache' });
  if (!res.ok) throw new Error(`status ${res.status}`);
  const body = (await res.json()) as { packages?: LicensedPackage[] };
  if (!Array.isArray(body.packages)) throw new Error('形が違います');
  return body.packages;
}

export function LicensesPage(): ReactNode {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  /**
   * この画面を管理画面の中から開いたか（ログインの画面・左の項目立て）。そのときの「戻る」は、
   * 開く前の画面へ戻す（issue #509）。住所を直接開いたときは戻る先が無いので、先頭（/）へ。
   */
  const openedFromApp = location.key !== 'default';

  useEffect(() => {
    let cancelled = false;
    loadLicenses()
      .then((packages) => {
        if (!cancelled) setState({ kind: 'ready', packages });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  function retry(): void {
    setState({ kind: 'loading' });
    setAttempt((n) => n + 1);
  }

  return (
    <main className="admin-licenses">
      <PageHeader
        title="ライセンス"
        description="この管理画面で使っているオープンソースソフトウェアと、それぞれの著作権表示・ライセンス文です。"
        actions={
          <Link
            className="ui-button ui-button-secondary"
            to="/"
            onClick={(event) => {
              if (!openedFromApp) return;
              event.preventDefault();
              void navigate(-1);
            }}
          >
            戻る
          </Link>
        }
      />

      {state.kind === 'loading' ? <LoadingBlock /> : null}
      {state.kind === 'failed' ? (
        <ResultBanner
          tone="error"
          message="ライセンスの一覧を読み込めませんでした。"
          nextStep="通信の状態を確かめて、もう一度お試しください。"
          action={
            <Button variant="secondary" compact onClick={retry}>
              もう一度読み込む
            </Button>
          }
        />
      ) : null}
      {state.kind === 'ready' ? (
        <>
          <p className="admin-licenses-lead">
            {`${state.packages.length}件の部品を使っています。「ライセンス文を見る」を押すと、全文が開きます。`}
            {'同じ内容は、'}
            <a href={LICENSES_TEXT}>テキストファイル</a>
            {'でもご覧いただけます。'}
          </p>
          <ul className="admin-licenses-list">
            {state.packages.map((pkg) => (
              <li key={`${pkg.name}@${pkg.version}`} className="admin-licenses-item">
                <h2>
                  {pkg.name} <span className="admin-licenses-version">{pkg.version}</span>
                </h2>
                <p className="admin-licenses-kind">ライセンスの種類: {pkg.license}</p>
                {pkg.note ? <p className="admin-licenses-note">{pkg.note}</p> : null}
                <details>
                  <summary>ライセンス文を見る</summary>
                  {pkg.texts.map((t) => (
                    <pre key={t.file} className="admin-licenses-text">
                      {t.text}
                    </pre>
                  ))}
                </details>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </main>
  );
}

/** 画面の下の方に置く「ライセンス」への入口（ログインの画面・左の項目立ての下）。 */
export function LicensesLink(): ReactNode {
  return (
    <p className="admin-licenses-link">
      <Link to="/licenses">ライセンス</Link>
    </p>
  );
}
